import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { UseFormSetError } from 'react-hook-form';
import { ErrorDialogState } from '../error-dialog/ErrorDialog';
import { authHeader, handleResponse } from '../queryClient';
import { ApiInstructorExerciseRequest } from '../types';
import { asZonelessString } from '../utils/formatTime';
import { setFormFieldError } from '../utils/setFormFieldError';
import { useActiveCompany } from '../utils/useCompany';
import { EditExerciseFormData } from './EditAssignmentDetails';

export const useSaveExercise = (
  exerciseId: string,
  setFieldError: UseFormSetError<EditExerciseFormData>,
  onSuccess: () => void,
) => {
  const [errorDialog, setErrorDialog] = useState<ErrorDialogState>({
    open: false,
  });

  const activeCompany = useActiveCompany();
  const queryClient = useQueryClient();

  const { isLoading: savingExercise, mutate: saveExercise } = useMutation<
    { exerciseId: string },
    Response,
    EditExerciseFormData
  >({
    async mutationFn({ limitNumberOfAttempts, timeRange, ...data }) {
      const body: Partial<ApiInstructorExerciseRequest> = {
        ...data,
        endTime: asZonelessString(data.endTime ?? undefined),
        maxNumberOfAttempts: limitNumberOfAttempts ? +data.maxNumberOfAttempts : null, // This is a PATCH, so we have to send the field
        startTime: asZonelessString(data.startTime),
      };

      return fetch(`/api/teach/companies/${activeCompany?.companyId}/scheduled-exercises/${exerciseId}`, {
        body: JSON.stringify(body),
        headers: { ...(await authHeader()), 'Content-Type': 'application/json' },
        method: 'PATCH',
      }).then(handleResponse);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries([
        { path: `teach/companies/${activeCompany?.companyId}/scheduled-exercises/${exerciseId}` },
      ]);
      onSuccess();
    },
    onError: async error => {
      const body = await error?.json();
      if (body.errors) {
        setFormFieldError(
          body.errors,
          ['title', 'description', 'endTime', 'startTime', 'timezone', 'visibility'],
          setFieldError,
        );
      }

      if (body.title ?? body.message) {
        setErrorDialog({ message: body.title ?? body.message, open: true });
      }
    },
  });

  return {
    errorDialog,
    saveExercise,
    savingExercise,
    setErrorDialog,
  };
};
